import { createContext, useState, useContext, useEffect, useRef } from 'react'
import ageVerifyDistricts from '@utils/ageVerifyDistricts'
import { useAuthContext } from './authContext'
import useStateRef from 'react-usestateref'
import axios from '@utils/webRequests'
import distance from '@turf/distance'

const CurrentLocationContext = createContext();
const CurrentLocationProvider = ({ children }) => {
  const { state: authState, initialAuthentication, FAILED_INITIAL_AUTH } = useAuthContext()
  const [geoDistrict, setGeoDistrict] = useState(null)
  const [trueLocation, setTrueLocation, trueLocationRef] = useStateRef(null)
  const [currentLocation, setCurrentLocation, currentLocationRef] = useStateRef(null)
  const [isLocationPermissionDenied, setIsLocationPermissionDenied] = useState(false)
  const [willRetryGetPosition, setWillRetryGetPosition] = useState(false)
  const [isRetryingGetPosition, setIsRetryingGetPosition] = useState(false)
  const intervalRef = useRef(null)

  // Sets the interval to continue retrieving the user's location for the map and nearby features
  useEffect(() => {
    const { travelModeEnabledAt, lonlat } = authState?.user || {}

    // Exit function if we don't have a user, or if travel mode is enabled with a valid current location
    if (!authState?.user || (travelModeEnabledAt && currentLocation)) return;

    // If travel mode is enabled, but the current location isn't valid, then set the current location to the user's set location
    // NOTE: This condition happens when we start the application with travel mode enabled
    if (travelModeEnabledAt && !currentLocation) {
      // set true location and geo district
      setGeoDistrictIfNone()
      // set current location to user's set location
      setCurrentLocation(lonlat)
    } else {
      // Under normal conditions, without travel mode enabled, we want to get the current position if not already set,
      // and then we initiate the interval to keep requesting it.
      if (!currentLocation) getPosition()
      clearInterval(intervalRef.current)
      intervalRef.current = setInterval(() => getPosition(), 30_000)
      return () => clearInterval(intervalRef.current);
    }
  }, [authState?.user, willRetryGetPosition])

  // NOTE: This is the logic that gets the users location to determine what state they are in.
  //       Turn this back on when applicable.

  // If the user isn't authenticated when they first navigate to the app
  // we want to retrieve what region/state they're in for legal purposes
  // useEffect(() => {
  //   if (!geoDistrict && initialAuthentication === FAILED_INITIAL_AUTH) {
  //     setGeoDistrictIfNone()
  //   }
  // }, [initialAuthentication, geoDistrict])

  const getPosition = () => {
    navigator.geolocation.getCurrentPosition((position) => {
      setIsLocationPermissionDenied(false)
      setIsRetryingGetPosition(false)
      const { longitude, latitude } = position.coords || {}
      const lonlat = [longitude, latitude]
      let distanceFromLastPosition = 0;
      if (trueLocation) {
        distanceFromLastPosition = distance(
          trueLocation,
          [longitude, latitude],
          { units: 'meters' },
        )
      }
      
      // Once we verify that conditions are met to set our location again,
      // Update our location in the backend and set the currentlocation to
      // the offset lonlat that we get back.
      if (!currentLocationRef.current || distanceFromLastPosition > 50) {
        const include_geo_district = !geoDistrict
        axios.put('/api/my/profiles/locations', { lonlat, include_geo_district })
          .then((res) => {
            setTrueLocation(lonlat)
            setCurrentLocation(res.data.lonlat)
            // If no geo district set previously before authentication, grab it now after authentication
            if (include_geo_district && res.data.geoDistrict) {
              setGeoDistrict(res.data.geoDistrict)
            }
          })
          .catch(console.log)
      }
    }, deniedLocationPermission)
  }

  const setTrueLocationIfNone = () => new Promise((resolve) => {
    // Short circuit if true location alredy exists
    if (trueLocation) {
      resolve(trueLocation)
      return
    }

    // Get current users position and set true location
    navigator.geolocation.getCurrentPosition((position) => {
      setIsLocationPermissionDenied(false)
      setIsRetryingGetPosition(false)
      const { longitude, latitude } = position.coords || {}
      const lonlat = [longitude, latitude]
      setTrueLocation(lonlat)
      resolve(lonlat)
    }, deniedLocationPermission)
  })

  const deniedLocationPermission = (error) => {
    // Error: Permission DENIED or Unavailable
    if (error.code === error.PERMISSION_DENIED) {
      console.log("USER DENIED LOCATION ACCESS.");
      setIsLocationPermissionDenied(true)
      setCurrentLocation(null)
      setTrueLocation(null)
      setIsRetryingGetPosition(false)
      // Prompt user to enable it manually via Safari Website Settings
    }
  }

  const setGeoDistrictIfNone = (lonlat) => {
    if (geoDistrict) return;
    lonlat ? geoDistrictRequest(lonlat) : setTrueLocationIfNone().then(geoDistrictRequest)
  }

  const geoDistrictRequest = (lonlat) => {
    axios.get('api/my/profiles/geo_districts', { params: { lonlat } })
      .then((res) => res.data && setGeoDistrict(res.data))
  }

  const isGeoDistrictRestricted = () => 
    (geoDistrict && process.env.NODE_ENV != 'production')
      ? ageVerifyDistricts.has(geoDistrict.district)
      : false

  const retryGetPosition = () => {
    setWillRetryGetPosition(!willRetryGetPosition)
    setIsRetryingGetPosition(true)
  }

  return (
    <CurrentLocationContext.Provider value={{
      setCurrentLocation,
      currentLocationRef,
      currentLocation,
      trueLocation,
      currentLocationExists: !!currentLocation,
      geoDistrict,
      isGeoDistrictRestricted,
      isLocationPermissionDenied,
      retryGetPosition,
      isRetryingGetPosition,
    }}>
      {children}
    </CurrentLocationContext.Provider>
  )
}

const useCurrentLocationContext = () => useContext(CurrentLocationContext)

export default useCurrentLocationContext

export { CurrentLocationContext, CurrentLocationProvider, useCurrentLocationContext }
